import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { http } from '@pendo/http';

export const DEFAULT_APPLICATION_ROLE = 'predefined_1';

export const REPLAY_USER_ROLE = 'predefined_9';

export const SUBSCRIPTION_USER_ROLE = 'predefined_sub_2';

export const SUBSCRIPTION_ADMIN_ROLE = 'predefined_sub_1';

export const SHARE_SEGMENTS_ROLE = 'predefined_sub_3';

export const VERIFY_SEGMENTS_ROLE = 'segment_approver_sub_role';

export const SHARE_REPORTS_ROLE = 'predefined_sub_4';

export const AI_AGENT_ADMIN_ROLE = 'predefined_8';

export const SUPER_ROLE = 'super';

export const GLOBAL_ACCESS_ROLE = 'global_access';

export const OPS_ROLE = 'ops';

export const SUPPORT_ROLE = 'support';

export const ADOPT_MANAGER_ROLE = 'adopt_manager_sub_role';

export const VOC_VIEWER_ROLE = 'voc_viewer_role';

export const VOC_MANAGER_ROLE = 'predefined_sub_6';

export const VOC_CONTRIBUTOR_ROLE = 'predefined_sub_7';

export const ROADMAP_VIEWER_ROLE = 'roadmap_viewer_sub_role';

export const ROADMAP_CREATOR_ROLE = 'roadmap_creator_sub_role';

export const ROADMAP_PUBLISHER_ROLE = 'roadmap_publisher_sub_role';

export const RESOURCE_CENTER_PERMISSION_SETS = ['7', '8', '9', '24'];

export const ROLE_MAP = {
    default: {
        name: 'Learn More About Roles & Permissions',
        description:
            'Hover over any of the default or custom roles above to learn about them. As you create custom roles, the descriptions will show in this area to provide a quick reference for you.',
    },
    sunrise_default: {
        name: 'Learn More About Roles & Permissions',
        description: 'Hover over any of the roles above to learn about them.',
    },
    predefined_1: {
        name: 'Viewer',
        description: ['This is the base level user role', 'Viewers can view metrics across the application'],
    },
    predefined_2: {
        name: 'Guide Publisher',
        description: ['Publish & Schedule Guides', 'Delete Non-Public Guides'],
    },
    predefined_3: {
        name: 'Guide Creator',
        description: [
            'Create & Edit Guides',
            'Copy and clone existing Guides',
            'Delete Non-Public Guides',
            'Create, Edit & Delete Pages and Features',
        ],
    },
    predefined_4: {
        name: 'Tag Editor',
        description: ['Create, Edit & Delete Pages and Features'],
    },
    predefined_5: {
        name: 'Guide Content Editor',
        description: [
            'Copy and clone existing Guides',
            'Edit Guides',
            'Delete Non-Public Guides',
            'Create, Edit & Delete Pages and Features',
        ],
    },
    predefined_6: {
        name: 'Resource Center Author',
        description: ['Create Resource Center', 'Edit Resource Center'],
    },
    predefined_7: {
        name: 'Resource Center Publisher',
        description: ['Promote a Resource Center to Production', 'Demote a Resource Center to Draft or Disabled'],
    },
    predefined_8: {
        name: 'AI Agent Admin',
        description: [
            'View visitor prompts',
            'View and edit agent settings',
            'Tag new agents and delete existing agents',
        ],
    },
    predefined_9: {
        name: 'Replay User',
        description: [
            'View replays',
            'Save clips',
            'Create playlists',
            'Create saved replay views',
            'Comment on replays',
        ],
    },
    voc_viewer_role: {
        name: 'Viewer',
        description: 'Can view feedback, ideas, and idea tests',
    },
    predefined_sub_6: {
        name: 'Manager',
        description: [
            'Create, edit, and delete feedback items and ideas',
            'Create idea tests',
            'Publish idea tests (with Guide Publisher permissions)',
            'Add ideas to roadmaps (with Roadmap Creator or Roadmap Publisher permissions)',
            'Promote ideas to roadmap features (with Roadmap Creator or Roadmap Publisher permissions)',
            'Change statuses on feedback items and ideas',
            'Add effort and impact ratings to ideas',
            'Remove any visitor votes from ideas',
            'Create, edit, and delete ideas portals',
            'Share ideas portals through a Resource Center (with Resource Center Author and Resource Center Publisher permissions)',
            'Share ideas portals through a guide (with Guide Publisher permissions)',
            'Send email updates to customers on feedback items and ideas',
        ],
        subHeading: 'A Listen Manager has Contributor role permissions plus the ability to:',
    },
    predefined_sub_7: {
        name: 'Contributor',
        description: [
            'Create feedback items',
            "Edit or delete feedback items that they've created",
            'Link feedback items to ideas',
            'Add visitor votes to ideas',
            "Remove votes they've added",
        ],
        subHeading: 'A Listen Contributor has Viewer role permissions plus the ability to:',
    },
    roadmap_viewer_sub_role: {
        name: 'Roadmap Viewer',
        description: 'Can view any roadmaps that are shared with them',
    },
    roadmap_creator_sub_role: {
        name: 'Roadmap Creator',
        description: [
            'Create roadmaps',
            'Delete roadmaps that they have created',
            'Edit roadmaps they have created or have edit access to',
            'Change statuses on roadmap features and initiatives that they have created or have access to',
            'Share roadmaps with other Pendo users',
        ],
        subHeading: 'A Roadmap Creator has Roadmap Viewer role permissions plus the ability to:',
    },
    roadmap_publisher_sub_role: {
        name: 'Roadmap Publisher',
        description: [
            'Share roadmaps externally using a public URL',
            'Publish updates to externally shared roadmaps',
            'Unpublish externally shared roadmaps',
        ],
        subHeading: 'A Roadmap Publisher has Roadmap Creator role permissions plus the ability to:',
    },
};

export const SENTIMENT_ROLE_MAP = {
    predefined_2: {
        name: 'Guide Publisher',
        description: ['Publish & Schedule Guides', 'Delete Guides in Draft, Staged, Disabled'],
        footnote: 'Sentiment surveys are included as part of guides',
    },
    predefined_3: {
        name: 'Guide Creator',
        description: [
            'Create & Edit Guides from Scratch',
            'Create & Edit Guides from Layouts (and Templates)',
            'Delete Guides in Draft, Staged, Disabled',
            'Create, Edit & Delete Pages and Features',
            'Triage NPS Themes - add, remove, edit and assign themes for NPS textual responses',
        ],
        footnote: 'Sentiment surveys are included as part of guides',
    },
    predefined_4: {
        name: 'Tagging Editor',
        description: ['Create, Edit & Delete Pages and Features', 'Edit & Delete Track Events'],
    },
    predefined_5: {
        name: 'Guide Content Editor',
        description: [
            'Create Guides from Themes and Layouts',
            'Delete Guides in Draft, Staged, Disabled',
            'Create, Edit & Delete Pages and Features',
            'Triage NPS Themes - add, remove, edit and assign themes for NPS textual responses',
        ],
        footnote: 'Sentiment surveys are included as part of guides',
    },
    predefined_6: {
        name: 'Resource Center Author',
        description: ['Create Resource Center', 'Edit Resource Center'],
    },
    predefined_7: {
        name: 'Resource Center Publisher',
        description: ['Promote a Resource Center to Production', 'Demote a Resource Center to Draft or Disabled'],
    },
};

export const NPS_ROLE_MAP = {
    predefined_2: {
        name: 'Guide Publisher',
        description: ['Publish & Schedule Guides', 'Delete Guides in Draft, Staged, Disabled'],
        footnote: 'NPS is included as part of guides',
    },
    predefined_3: {
        name: 'Guide Creator',
        description: [
            'Create & Edit Guides from Scratch',
            'Create & Edit Guides from Layouts (and Templates)',
            'Delete Guides in Draft, Staged, Disabled',
            'Create, Edit & Delete Pages and Features',
            'Triage NPS Themes - add, remove, edit and assign themes for NPS textual responses',
        ],
        footnote: 'NPS is included as part of guides',
    },
    predefined_4: {
        name: 'Tagging Editor',
        description: ['Create, Edit & Delete Pages and Features', 'Edit & Delete Track Events'],
    },
    predefined_5: {
        name: 'Guide Content Editor',
        description: [
            'Create Guides from Themes and Layouts',
            'Delete Guides in Draft, Staged, Disabled',
            'Create, Edit & Delete Pages and Features',
            'Triage NPS Themes - add, remove, edit and assign themes for NPS textual responses',
        ],
        footnote: 'NPS is included as part of guides',
    },
    predefined_6: {
        name: 'Resource Center Author',
        description: ['Create Resource Center', 'Edit Resource Center'],
    },
    predefined_7: {
        name: 'Resource Center Publisher',
        description: ['Promote a Resource Center to Production', 'Demote a Resource Center to Draft or Disabled'],
    },
};

export const SEGMENT_VERIFICATION_OPTION = {
    id: VERIFY_SEGMENTS_ROLE,
    label: 'Verify segments',
    helperText: 'Ability to change verification of segments and respond to verification requests.',
    tooltip: 'User must have edit permission in order to verify segments',
    disabled: true,
};

export const SHARE_OPTIONS = [
    {
        id: SHARE_SEGMENTS_ROLE,
        label: 'Share and edit segments with everyone',
        helperText: 'Share segments you’ve created with everyone, and edit segments that have been shared with you.',
        childOptions: [SEGMENT_VERIFICATION_OPTION],
    },
    {
        id: SHARE_REPORTS_ROLE,
        label: 'Share and edit reports with everyone',
        helperText: 'Share reports you’ve created with everyone, and edit reports that have been shared with you.',
    },
];

export const VOC_OPTIONS = [
    {
        id: VOC_VIEWER_ROLE,
        description: 'Viewer',
        name: 'Viewer',
        isDisabled: true,
        selectionState: 'checked',
    },
    {
        id: VOC_CONTRIBUTOR_ROLE,
        description: 'Contributor',
        name: 'Contributor',
        selectionState: 'checked',
    },
    {
        id: VOC_MANAGER_ROLE,
        description: 'Manager',
        name: 'Manager',
    },
];

export const ROADMAP_OPTIONS = [
    {
        id: ROADMAP_VIEWER_ROLE,
        description: 'Roadmap Viewer',
        name: 'Roadmap Viewer',
        isDisabled: true,
        selectionState: 'checked',
    },
    {
        id: ROADMAP_CREATOR_ROLE,
        description: 'Roadmap Creator',
        name: 'Roadmap Creator',
        selectionState: 'unchecked',
    },
    {
        id: ROADMAP_PUBLISHER_ROLE,
        description: 'Roadmap Publisher',
        name: 'Roadmap Publisher',
        selectionState: 'unchecked',
    },
];

export const SELECTION_STATE = {
    checked: 'checked',
    indeterminate: 'indeterminate',
    unchecked: 'unchecked',
};

// The roles in this array are not retrieved or sent to the backend
// take special care to filter these out prior to updates, or to add them to the BE results when retrieving roles
export const SUPPLEMENTAL_ROLES = [
    SUBSCRIPTION_USER_ROLE,
    SUPER_ROLE,
    OPS_ROLE,
    SUPPORT_ROLE,
    GLOBAL_ACCESS_ROLE, // check
    ADOPT_MANAGER_ROLE,
    VOC_VIEWER_ROLE,
    ROADMAP_VIEWER_ROLE,
];

// Helper function determines if a role is managed through the api
// ASSUME yes unless the role is one of the ones we list above
export function isRoleManagedByAPI (role) {
    return !SUPPLEMENTAL_ROLES.includes(role);
}

export function isAdoptSub (subscriptionConfig) {
    return subscriptionConfig.isAdopt || subscriptionConfig.isAEU;
}

export function hasResourceCenterPermissionSet (role) {
    if (!role.permissionSets) {
        return false;
    }
    if (role.id === 'predefined_6' || role.id === 'predefined_7') {
        return true;
    }

    return role.permissionSets.some((set) => RESOURCE_CENTER_PERMISSION_SETS.includes(set));
}

export function buildRoleMapBy (predefinedRoles, prop) {
    return predefinedRoles.reduce(
        (roleNameMap, role) => ({
            [role.id]: prop ? role[prop] : role,
            ...roleNameMap,
        }),
        {},
    );
}

export function buildRoleMapWithRoles (roles) {
    const predefinedRoles = roles.filter(({ type }) => type === 'predefined');
    const customRoles = roles.filter(({ type, name }) => type === 'custom' && Boolean(name));

    return buildRoleMapBy([...customRoles, ...predefinedRoles], 'name');
}

export function buildRoleList (roleIdList, roleNameMap) {
    if (!roleIdList || roleIdList.length === 0) {
        return '';
    }

    const predefinedRoles = [];
    const saveCustomRoles = cloneDeep(roleIdList);

    roleIdList.forEach((role) => {
        if (role.includes('predefined')) {
            predefinedRoles.push(role);
            saveCustomRoles.splice(saveCustomRoles.indexOf(role), 1);
        }
    });

    predefinedRoles.sort();
    const sortedRoleIdList = [...predefinedRoles, ...saveCustomRoles];

    return sortedRoleIdList.reduce((roleNames, id) => {
        if (roleNameMap[id]) {
            roleNames.push(roleNameMap[id]);
        }

        return roleNames;
    }, []);
}

export function buildRoleListDisplayName (roleIdList, roleNameMap) {
    if (!roleIdList || roleIdList.length === 0) {
        return '';
    }

    return buildRoleList(roleIdList, roleNameMap).join(', ');
}

export function buildApplicationPermissionsMap (user, apps, canSelectViewer = false) {
    if (!isEmpty(user) && user.applicationRoles && !isEmpty(user.applicationRoles)) {
        // `user.applicationRoles` array will need to be filtered in the future to handle custom roles
        // role.type === `custom` || `predefined` exists to do so
        let hasDefaultRole = false;
        const applicationRoles = cloneDeep(user.applicationRoles);

        // build `{ appId: [ ...roleIds ] }`
        return apps.reduce((appMap, { id }) => {
            // empty array of the app doesn't exist in the list because new or other
            const applicationRolesByApp = applicationRoles[id] || [];

            // create roleId list and check if the user already has default role
            const roleIdList = applicationRolesByApp.map((role) => {
                const id = role.id || role;
                if (id === DEFAULT_APPLICATION_ROLE) {
                    hasDefaultRole = true;
                }

                return id;
            });

            if (!canSelectViewer && (isEmpty(applicationRolesByApp) || !hasDefaultRole)) {
                // add at the front
                roleIdList.unshift(DEFAULT_APPLICATION_ROLE);
            }

            return {
                [id]: roleIdList,
                ...appMap,
            };
        }, {});
    }

    return apps.reduce((appMap, { id }) => {
        const defaultRoles = [!canSelectViewer && DEFAULT_APPLICATION_ROLE].filter(Boolean);

        return {
            [id]: defaultRoles,
            ...appMap,
        };
    }, {});
}

export function getApplicationRolesBySub (subId, type = '') {
    return http.get(`/api/s/${subId}/applicationrole?type=${type}`).then((res) => res.data);
}

/**
 * These nested loops check each application role a user has in each application and removes any custom role that doesn't exist.
 * This only checks users (non-admins), since admins don't have custom roles
 */
export function removeNonExistentCustomRoles (userList, roleNameMap) {
    userList.forEach((user) => {
        if (user.applicationRoles && !user.flags?.admin) {
            for (const application of Object.values(user.applicationRoles)) {
                for (const [index, roleId] of application.entries()) {
                    if (!roleId.includes('predefined') && !Object.keys(roleNameMap).includes(roleId)) {
                        application.splice(index, 1);
                    }
                }
            }
        }
    });
}

export function isRoleDisabled (role, selectedApps, disableRCRoles = false, canSelectViewer = false, viewerSelected = false, isRemoveMode = false) {
    return (
        (role.id === DEFAULT_APPLICATION_ROLE && !canSelectViewer) ||
        (role.id !== DEFAULT_APPLICATION_ROLE && viewerSelected && isRemoveMode) ||
        selectedApps.length === 0 ||
        (disableRCRoles && hasResourceCenterPermissionSet(role)) ||
        (role.id === REPLAY_USER_ROLE && isRemoveMode && selectedApps.every((app) => !app.enableSessionReplay && !app.applicationFlags?.enableSessionReplay)) ||
        (role.id === REPLAY_USER_ROLE && !isRemoveMode && selectedApps.some((app) => !app.enableSessionReplay && !app.applicationFlags?.enableSessionReplay))
    );
}

export function getRoleSelectionStateAndAppNames (
    selectedApps,
    role,
    applicationPermissionsMap,
    disableRCRoles = false,
    canSelectViewer = false,
) {
    const { id: roleId } = role;

    const selectionStateAndAppNames = {
        selectionState: SELECTION_STATE.unchecked,
        isDisabled: isRoleDisabled(role, selectedApps, disableRCRoles, canSelectViewer),
        appNames: '',
        appsWithoutRC: '',
        appsWithSessionReplay: '',
        numAppsWithSessionReplay: 0,
        numApps: 0,
    };

    if (selectedApps.length === 0) {
        return selectionStateAndAppNames;
    }

    const appNames = [];
    const appsWithoutRC = [];
    const appsWithSessionReplay = [];
    selectedApps.forEach((app) => {
        if (applicationPermissionsMap[app.id].indexOf(roleId) > -1) {
            // Hide the Replay User role in case Session Replay has been disabled
            if (app.enableSessionReplay || roleId !== REPLAY_USER_ROLE) {
                appNames.push(app.displayName);
            }
        }
        if (!app.resourceCenterAccess) {
            appsWithoutRC.push(app.displayName);
        }
        if (app.enableSessionReplay) {
            const truncatedDisplayName =
                app.displayName.length > 39 ? `${app.displayName.slice(0, 36)}...` : app.displayName;
            appsWithSessionReplay.push(truncatedDisplayName);
        }
    });

    const prepend = appsWithSessionReplay.length > 0 ? ':\n- ' : '';

    // turn appNames into a string for display
    selectionStateAndAppNames.appNames = appNames.join(', ');
    selectionStateAndAppNames.appsWithoutRC = appsWithoutRC.join(', ');
    selectionStateAndAppNames.numAppsWithSessionReplay = appsWithSessionReplay.length;
    selectionStateAndAppNames.appsWithSessionReplay = `${prepend}${
        appsWithSessionReplay.length > 7
            ? `${appsWithSessionReplay.slice(0, 6).join('\n- ')}\n+ ${appsWithSessionReplay.length - 6} more`
            : appsWithSessionReplay.join('\n- ')
    }`;
    selectionStateAndAppNames.numApps = selectedApps.length;
    const autoEnableViewer = roleId === DEFAULT_APPLICATION_ROLE && !canSelectViewer;
    if (autoEnableViewer || appNames.length === selectedApps.length) {
        selectionStateAndAppNames.selectionState = SELECTION_STATE.checked;
    } else if (appNames.length > 0) {
        selectionStateAndAppNames.selectionState = SELECTION_STATE.indeterminate;
    }

    return selectionStateAndAppNames;
}
